import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  Scale,
  Award,
  Users,
  Building2,
  Landmark,
  GraduationCap,
  CreditCard,
  Home as HomeIcon,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  MessageCircle,
  Sparkles,
  ChevronRight,
  Newspaper,
  X,
  Maximize2,
  BadgeCheck,
  FileText,
  AlertCircle,
  Copy,
  Check,
  Send,
  Calendar,
} from "lucide-react";

import VocHero from "../../assets/VoiceOfConsumer/voc-hero.jpeg";
import VocMasthead from "../../assets/VoiceOfConsumer/voc-feature-1.jpeg";
import VocMic from "../../assets/VoiceOfConsumer/voc-feature-2.jpeg";
import seoData from "../../assets/data/seo.json";

const VoiceOfConsumers = () => {
  const seo = seoData.voice_of_consumers || {
    title: "Voice of Consumer | Consumer Rights Council | CRC Bharat",
    description:
      "Voice of Consumer is a media initiative of Consumer Rights Council (CRC Bharat), registered with the Press Registrar General of India, empowering citizens through responsible journalism.",
    keywords:
      "Voice of Consumer, CRC Bharat newspaper, consumer protection journalism, Dr Vikas Pandey, APBIL/26/A7269",
    canonical: "https://crcbharat.com/voice-of-consumers",
    ogImage: "https://crcbharat.com/images/Thumbmail-demo.png",
  };

  const [activeModalImage, setActiveModalImage] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const registrationDetails = [
    { label: "Newspaper Title", value: "VOICE OF CONSUMER" },
    { label: "Registration Number", value: "APBIL/26/A7269", highlight: true },
    {
      label: "Registration Authority",
      value: "Press Registrar General of India (PRGI), Govt. of India",
    },
    { label: "Periodicity", value: "Monthly Publication" },
    { label: "Language(s)", value: "English & Telugu" },
    { label: "Publisher & Editor", value: "Dr. Vikas Pandey" },
    { label: "Place of Publication", value: "Visakhapatnam, Andhra Pradesh" },
    { label: "Governing Act", value: "Press & Registration of Periodicals Act, 2023" },
  ];

  const missionPoints = [
    {
      id: 1,
      title: "Consumer Rights & Responsibilities",
      description: "Promoting widespread awareness of consumer rights and civic responsibilities.",
      icon: ShieldCheck,
      color: "from-blue-600 to-indigo-700",
    },
    {
      id: 2,
      title: "Highlighting Public Grievances",
      description: "Giving visibility to genuine consumer grievances and issues affecting everyday citizens.",
      icon: AlertCircle,
      color: "from-amber-500 to-orange-600",
    },
    {
      id: 3,
      title: "Educating on Consumer Protection Act, 2019",
      description: "Demystifying the Consumer Protection Act, 2019, and other crucial consumer welfare statutes.",
      icon: Scale,
      color: "from-emerald-600 to-teal-700",
    },
    {
      id: 4,
      title: "Reporting Violations & Unfair Trade",
      description:
        "Investigating misleading advertisements, adulterated products, counterfeit goods, and market malpractice.",
      icon: Newspaper,
      color: "from-rose-600 to-red-700",
    },
    {
      id: 5,
      title: "Promoting Market Transparency",
      description: "Encouraging ethical corporate conduct, fair business practices, and social accountability.",
      icon: Building2,
      color: "from-sky-600 to-blue-800",
    },
    {
      id: 6,
      title: "Expert Views & Public-Interest Reports",
      description: "Publishing well-researched investigative pieces, expert legal columns, and special reports.",
      icon: FileText,
      color: "from-purple-600 to-indigo-800",
    },
    {
      id: 7,
      title: "Multi-Platform Community Outreach",
      description: "Strengthening grassroots awareness through print editions, digital portals, and seminars.",
      icon: Globe,
      color: "from-cyan-600 to-blue-700",
    },
  ];

  const coverageAreas = [
    {
      title: "Consumer Rights and Protection",
      description:
        "News and information concerning consumer rights, complaints, statutory remedies, and consumer dispute redressal mechanisms across district, state, and national commissions.",
      icon: Scale,
      tag: "Core Focus",
      color: "border-blue-500 text-blue-600 bg-blue-50/70",
    },
    {
      title: "Business and Market Practices",
      description:
        "In-depth reports on unfair trade practices, misleading advertisements, deceptive offers, product quality assurance, honest pricing, and transparency in retail and e-commerce.",
      icon: Building2,
      tag: "Marketplace",
      color: "border-amber-500 text-amber-600 bg-amber-50/70",
    },
    {
      title: "Banking and Financial Services",
      description:
        "Consumer issues relating to retail banking, insurance claims, hidden charges, loan mis-selling, digital payment fraud, fintech platforms, and financial Ombudsman services.",
      icon: CreditCard,
      tag: "Finance",
      color: "border-emerald-500 text-emerald-600 bg-emerald-50/70",
    },
    {
      title: "Real Estate and Housing",
      description:
        "Ground reality reports concerning home buyers, delayed project deliveries, builder-buyer contractual obligations, RERA compliance, and housing society disputes.",
      icon: HomeIcon,
      tag: "Real Estate",
      color: "border-indigo-500 text-indigo-600 bg-indigo-50/70",
    },
    {
      title: "Education and Healthcare",
      description:
        "Public-interest reporting on commercialization of education, illegal fees, student welfare, healthcare billing standards, hospital transparency, and patient rights.",
      icon: GraduationCap,
      tag: "Social Welfare",
      color: "border-rose-500 text-rose-600 bg-rose-50/70",
    },
    {
      title: "Cyber Fraud and Digital Safety",
      description:
        "High-priority awareness articles on online scams, OTP frauds, UPI & payment gateway theft, identity impersonation, and safe cybersecurity hygiene for modern digital consumers.",
      icon: ShieldCheck,
      tag: "Digital Security",
      color: "border-purple-500 text-purple-600 bg-purple-50/70",
    },
    {
      title: "Government Policies and Public Interest",
      description:
        "Timely news, critical analysis, and citizen-first breakdowns of government notifications, legislative regulations, and public initiatives that impact consumers.",
      icon: Landmark,
      tag: "Policy & Law",
      color: "border-cyan-500 text-cyan-600 bg-cyan-50/70",
    },
  ];

  const journalismPillars = [
    {
      title: "Fact Verification",
      desc: "Thoroughly verifying claims, evidence, and public documents before publishing.",
    },
    {
      title: "Right to Respond",
      desc: "Providing a fair opportunity for relevant parties and authorities to respond to allegations.",
    },
    {
      title: "Dignity & Privacy",
      desc: "Upholding the dignity and confidentiality of affected consumers and whistleblowers.",
    },
    {
      title: "Non-Sensationalism",
      desc: "Zero clickbait or prejudice on matters under investigation or before competent judicial forums.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 selection:bg-blue-600 selection:text-white">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content={seo.ogImage} />
        <meta property="og:url" content={seo.canonical} />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.ogImage} />
      </Helmet>

      {/* ==================== 1. TOP OFFICIAL MASTHEAD STRIP ==================== */}
      <section className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 border-b border-blue-800/40 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                <Sparkles className="w-3 h-3 text-amber-400" />
                OFFICIAL PRESS INITIATIVE
              </span>
              <span className="text-slate-300 hidden md:inline">
                Consumer Rights Council (CRC Bharat)
              </span>
            </div>

            <div className="flex items-center gap-4 text-slate-300 text-xs">
              <span className="flex items-center gap-1.5">
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-slate-200 font-medium">PRGI Reg:</span>
                <span className="font-mono text-amber-300 font-bold">APBIL/26/A7269</span>
              </span>
              <span className="hidden sm:inline text-slate-500">|</span>
              <span className="hidden sm:inline text-slate-300">
                English &amp; Telugu • Monthly Edition
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 2. NEWSPAPER HEADER BANNER (MASTHEAD) ==================== */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col items-center justify-center">
          <div className="w-full max-w-3xl flex justify-center">
            <img
              src={VocMasthead}
              alt="VOICE OF CONSUMER - A Newspaper for a Stronger, Fairer Consumer Nation"
              className="max-h-24 sm:max-h-32 w-auto object-contain drop-shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
              onClick={() => setActiveModalImage(VocMasthead)}
              title="Click to view full masthead"
            />
          </div>
          <div className="w-full border-t border-slate-200 mt-2 pt-2 flex flex-wrap justify-between items-center text-[11px] sm:text-xs text-slate-500 font-medium tracking-wide">
            <span>A MEDIA INITIATIVE OF CONSUMER RIGHTS COUNCIL (CRC BHARAT)</span>
            <span className="text-blue-800 font-semibold">YOUR RIGHTS • YOUR VOICE • YOUR NEWSPAPER</span>
            <span>PRESS REGISTRAR GENERAL OF INDIA REGD.</span>
          </div>
        </div>
      </div>

      {/* ==================== 3. HERO SHOWCASE SECTION ==================== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-950 via-slate-900 to-blue-950 text-white py-12 lg:py-16">
        {/* Background Ambient Glows */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Headlines & Presentation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-medium">
                <Newspaper className="w-4 h-4 text-amber-400" />
                <span>Responsible Consumer Journalism &amp; Public Accountability</span>
              </div>

              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white uppercase">
                  VOICE OF CONSUMER
                </h1>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-amber-400 mt-2">
                  A Media Initiative of Consumer Rights Council – CRC Bharat
                </h3>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <p className="text-base sm:text-lg font-bold text-slate-100 italic">
                  &ldquo;Giving Consumers a Voice. Promoting Awareness. Protecting Rights.&rdquo;
                </p>
                <p className="text-sm text-blue-200/90 mt-2 leading-relaxed">
                  Voice of Consumer connects consumers, ethical businesses, public authorities, and
                  communities across India through credible, verified reporting and advocacy.
                </p>
              </div>

              {/* Quick Credential Highlights */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-lg p-3">
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider">Registration</div>
                  <div className="text-sm font-bold text-amber-300 font-mono mt-0.5">APBIL/26/A7269</div>
                  <div className="text-[10px] text-slate-400">Press Registrar General</div>
                </div>
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-lg p-3">
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider">Periodicity</div>
                  <div className="text-sm font-bold text-white mt-0.5">Monthly Edition</div>
                  <div className="text-[10px] text-slate-400">English &amp; Telugu</div>
                </div>
                <div className="col-span-2 sm:col-span-1 bg-slate-800/60 border border-slate-700/60 rounded-lg p-3">
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider">Reach</div>
                  <div className="text-sm font-bold text-emerald-400 mt-0.5">Pan-India</div>
                  <div className="text-[10px] text-slate-400">Every District &amp; State</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="#submit-news"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 transition-all hover:scale-105 active:scale-95"
                >
                  <Send className="w-4 h-4" />
                  Submit News &amp; Issues
                </a>
                <a
                  href="#join-reporters"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-blue-700 hover:bg-blue-600 text-white font-semibold text-sm shadow-lg shadow-blue-700/20 transition-all hover:scale-105 active:scale-95"
                >
                  <Users className="w-4 h-4" />
                  Join Reporter Network
                </a>
           
              </div>
            </motion.div>

            {/* Right Column: Hero Poster Showcase with Click-to-Enlarge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5 flex flex-col items-center"
            >
              <div className="relative group w-full max-w-lg bg-gradient-to-b from-slate-800 to-slate-900 p-2 sm:p-3 rounded-2xl shadow-2xl border border-slate-700/70">
                {/* Badge Overlay */}
                <div className="absolute -top-3 -right-3 z-20 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 text-xs font-extrabold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 border border-amber-300">
                  <BadgeCheck className="w-4 h-4" />
                  Govt. of India Certified
                </div>

                <div
                  className="relative overflow-hidden rounded-xl cursor-pointer bg-slate-950"
                  onClick={() => setActiveModalImage(VocHero)}
                >
                  <img
                    src={VocHero}
                    alt="Voice of Consumer Official Newspaper & Registration Poster"
                    className="w-full h-auto object-cover rounded-xl transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-blue-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2 text-white p-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                      <Maximize2 className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-semibold text-sm">Click to Inspect Full Resolution</span>
                    <span className="text-xs text-blue-200 text-center">
                      View PRGI Certificate of Registration &amp; Official Details
                    </span>
                  </div>
                </div>

                <div className="p-3 text-center">
                  <p className="text-xs text-slate-300 font-medium">
                    Official Announcement &amp; Registration Certificate
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Press Registrar General of India • Registration No:{" "}
                    <span className="text-amber-400 font-mono font-bold">APBIL/26/A7269</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== 4. OFFICIAL REGISTRATION DETAILS CARD ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 relative z-20">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
            <div>
              <span className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4 text-blue-700" />
                Statutory Information
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-1">
                Official Registration &amp; Publication Credentials
              </h2>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 text-blue-900 px-3.5 py-1.5 rounded-lg text-xs font-semibold border border-blue-100">
              <Calendar className="w-4 h-4 text-blue-700" />
              <span>Registered on 24 August 2026</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pt-6">
            {registrationDetails.map((item, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-xl border transition-all ${
                  item.highlight
                    ? "bg-amber-50/70 border-amber-200 shadow-sm"
                    : "bg-slate-50/80 border-slate-200/70 hover:bg-slate-100/60"
                }`}
              >
                <div className="text-xs text-slate-500 font-medium">{item.label}</div>
                <div
                  className={`mt-1 font-bold text-sm ${
                    item.highlight ? "text-amber-900 font-mono text-base" : "text-slate-800"
                  }`}
                >
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 5. ABOUT VOICE OF CONSUMER ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                Empowering Citizens
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">
                About Voice of Consumer
              </h2>
            </div>

            <div className="prose prose-slate max-w-none text-slate-700 space-y-4 text-base sm:text-lg leading-relaxed">
              <p>
                <strong className="text-blue-900 font-bold">VOICE OF CONSUMER</strong> is a media
                initiative of the Consumer Rights Council (CRC Bharat), established with the objective
                of promoting consumer awareness, educating the public, highlighting consumer
                grievances, and strengthening consumer protection across India.
              </p>
              <p>
                The newspaper aims to serve as a platform for responsible journalism, public
                awareness, and the dissemination of information relating to consumer rights, public
                interest, and social accountability. Through news reports, articles, investigative
                features, expert opinions, and awareness campaigns,{" "}
                <em className="text-blue-900 font-medium">Voice of Consumer</em> seeks to empower
                citizens with the knowledge they need to make informed decisions and protect
                themselves against unfair trade practices.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="w-10 h-10 rounded-lg bg-blue-800 text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Consumer Defense</h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Real advocacy against deceitful market tactics and substandard services.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100">
                <div className="w-10 h-10 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                  <Newspaper className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Citizen Journalism</h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    District-level reporting ensuring grassroots complaints reach policymakers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center">
            <div className="relative max-w-md w-full bg-gradient-to-tr from-blue-900 to-indigo-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl overflow-hidden">
              <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-blue-600/30 rounded-full blur-2xl" />
              
              <div className="relative z-10 space-y-6">
                <div className="inline-flex p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20">
                  <Award className="w-8 h-8 text-amber-400" />
                </div>

                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-amber-300">
                    CRC Bharat Flagship Media
                  </span>
                  <h3 className="text-2xl font-black text-white mt-1">
                    Your Rights. Your Voice. Your Newspaper.
                  </h3>
                </div>

                <p className="text-sm text-blue-100 leading-relaxed">
                  Registered under the Press and Registration of Periodicals Act, 2023. Publishing in
                  both English and Telugu to reach urban and rural readers alike.
                </p>

                <div className="border-t border-white/20 pt-4 space-y-2 text-xs text-blue-200">
                  <div className="flex items-center justify-between">
                    <span>Editor-in-Chief:</span>
                    <strong className="text-white">Dr. Vikas Pandey</strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Periodicity:</span>
                    <strong className="text-white">Monthly Circulation</strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Publisher:</span>
                    <strong className="text-white">Consumer Rights Council</strong>
                  </div>
                </div>

                <div className="pt-2">
                  <Link
                    to="/aboutus"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    <span>Learn more about CRC Bharat</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 6. OUR VISION & MISSION ==================== */}
      <section className="bg-slate-100 py-16 sm:py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16">
          {/* Vision Card */}
          <div className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 rounded-3xl p-8 sm:p-12 text-white shadow-xl overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-4">
              <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-bold tracking-wider uppercase bg-amber-400/20 text-amber-300 border border-amber-400/30">
                <Sparkles className="w-3.5 h-3.5" />
                Guiding Compass
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white">Our Vision</h2>
              <blockquote className="text-lg sm:text-2xl font-medium text-slate-100 leading-relaxed italic pt-2">
                &ldquo;To build an informed, empowered, and responsible consumer society in which
                every citizen understands their rights and has access to reliable information to make
                informed choices.&rdquo;
              </blockquote>
            </div>
          </div>

          {/* Mission Section */}
          <div className="space-y-8">
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                Core Objectives
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Our Mission
              </h2>
              <p className="text-slate-600 text-sm sm:text-base">
                Seven foundational commitments driving every edition, article, and grassroots initiative
                of Voice of Consumer.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {missionPoints.map((item) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={item.id}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border border-slate-200/80 flex flex-col justify-between"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div
                          className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center shadow-md`}
                        >
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <span className="text-xs font-bold font-mono text-slate-400">
                          0{item.id}
                        </span>
                      </div>
                      <h3 className="font-bold text-slate-900 text-lg">{item.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                    </div>
                  </motion.div>
                );
              })}

              {/* Special Summary Card */}
              <div className="bg-gradient-to-br from-blue-900 to-indigo-950 text-white rounded-2xl p-6 shadow-md border border-blue-800 flex flex-col justify-between">
                <div className="space-y-3">
                  <span className="text-xs font-bold text-amber-300 uppercase tracking-wider">
                    Community Impact
                  </span>
                  <h3 className="font-black text-xl text-white">Join the Cause</h3>
                  <p className="text-xs text-blue-200 leading-relaxed">
                    Voice of Consumer is more than a newspaper—it is a citizen-driven watchdog.
                    Strengthen consumer rights in your district today.
                  </p>
                </div>
                <div className="pt-4">
                  <a
                    href="#join-reporters"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 hover:text-amber-200 transition-colors"
                  >
                    <span>Become a Contributor</span>
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 7. WHAT WE COVER ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
            Key Editorial Pillars
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            What We Cover
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            Comprehensive, public-interest coverage across crucial sectors where consumer interests
            and ethical market practices matter most.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coverageAreas.map((area, idx) => {
            const Icon = area.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-sm hover:shadow-md hover:border-blue-300 transition-all flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-xl border ${area.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-100 px-2.5 py-1 rounded-full">
                      {area.tag}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{area.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{area.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ==================== 8. COMMITMENT TO RESPONSIBLE JOURNALISM ==================== */}
      <section className="bg-slate-900 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-950/40 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10 space-y-10">
          <div className="text-center space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <ShieldCheck className="w-3.5 h-3.5" />
              ETHICAL STANDARDS
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-white">
              Our Commitment to Responsible Journalism
            </h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-10 backdrop-blur-sm space-y-6">
            <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
              Voice of Consumer is committed to responsible, ethical, and public-interest-oriented
              journalism. We aim to present information fairly, verify facts to the best of our
              ability, respect the dignity and privacy of individuals, and provide an opportunity for
              relevant parties to respond to allegations wherever appropriate.
            </p>
            <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
              Our objective is to inform, educate, and raise awareness—not to sensationalise issues or
              prejudge matters that are under investigation or before a competent authority.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-white/10">
              {journalismPillars.map((p, i) => (
                <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/5">
                  <h4 className="text-sm font-bold text-amber-400">{p.title}</h4>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 9. JOIN OUR NETWORK OF REPORTERS ==================== */}
      <section id="join-reporters" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 scroll-mt-24">
        <div className="bg-gradient-to-br from-blue-50 via-white to-amber-50/40 rounded-3xl border border-blue-200/80 p-8 sm:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 flex justify-center">
              <div className="relative group max-w-xs">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-amber-500 rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity" />
                <img
                  src={VocMic}
                  alt="Voice of Consumer Microphone - Join Reporter Network"
                  className="relative rounded-2xl shadow-xl w-full h-auto object-contain bg-white p-4 border border-slate-200"
                />
              </div>
            </div>

            <div className="lg:col-span-8 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                  District &amp; State Level Opportunities
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">
                  Join Our Network of Reporters
                </h2>
              </div>

              <div className="space-y-4 text-slate-700 text-base leading-relaxed">
                <p>
                  Voice of Consumer invites applications from aspiring journalists, freelance
                  reporters, media professionals, and public-interest contributors who wish to work
                  towards a more informed and empowered society.
                </p>
                <p>
                  We are looking to build a network of reporters and contributors across{" "}
                  <strong className="text-blue-900 font-bold">
                    every district and state of India
                  </strong>{" "}
                  to bring important local issues, consumer grievances, and public-interest stories to
                  wider attention.
                </p>
                <p className="text-sm text-slate-600">
                  Interested individuals may contact our editorial team for further information
                  regarding reporting opportunities, editorial guidelines, and the application process.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="https://wa.me/919963633653?text=Hello%20Voice%20of%20Consumer%20Editorial%20Team%2C%20I%20am%20interested%20in%20joining%20as%20a%20District%2FState%20Reporter%20or%20Contributor."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all hover:scale-105"
                >
                  <MessageCircle className="w-4 h-4" />
                  Apply via WhatsApp
                </a>
                <a
                  href="mailto:news@crcbharat.com?subject=Application%20for%20Reporter%20%2F%20Contributor%20-%20Voice%20of%20Consumer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-semibold text-sm shadow-md transition-all hover:scale-105"
                >
                  <Mail className="w-4 h-4" />
                  Email Your Profile
                </a>
                <Link
                  to="/become-a-member"
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-medium text-sm transition-colors"
                >
                  <Users className="w-4 h-4 text-blue-700" />
                  Become CRC Member
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 10. SUBMIT YOUR NEWS AND CONSUMER ISSUES ==================== */}
      <section id="submit-news" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 scroll-mt-24">
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 shadow-xl border border-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                <Send className="w-3.5 h-3.5" />
                CITIZEN WHISTLEBLOWER &amp; GRIEVANCE DESK
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white">
                Submit Your News and Consumer Issues
              </h2>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 backdrop-blur-sm space-y-5 text-slate-200">
              <p className="text-base sm:text-lg font-semibold text-white leading-relaxed">
                Have you experienced an unfair trade practice, misleading advertisement, defective
                product, poor service, or another issue affecting consumers?
              </p>
              <p className="text-sm sm:text-base leading-relaxed text-slate-300">
                You may contact our editorial team to share information, news tips, articles, or
                public-interest concerns for editorial consideration. Please provide supporting
                documents or evidence wherever available, while protecting confidential and personal
                information.
              </p>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs sm:text-sm text-amber-200 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Editorial Policy:</strong> Submission of a report or grievance does not
                  guarantee publication. All submissions are subject to rigorous editorial review and
                  independent verification.
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a
                href="https://wa.me/919963633653?text=Hello%20Voice%20of%20Consumer%2C%20I%20would%20like%20to%20submit%20a%20consumer%20news%20tip%20%2F%20grievance."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all hover:scale-105"
              >
                <MessageCircle className="w-5 h-5" />
                Send via WhatsApp
              </a>

              <a
                href="mailto:news@crcbharat.com?subject=Consumer%20News%20Tip%20%2F%20Grievance%20Submission"
                className="flex items-center justify-center gap-2 p-4 rounded-xl bg-blue-700 hover:bg-blue-600 text-white font-bold text-sm shadow-md transition-all hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                Email News Tips
              </a>

              <Link
                to="/file-case"
                className="flex items-center justify-center gap-2 p-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-md transition-all hover:scale-105"
              >
                <FileText className="w-5 h-5" />
                File Formal Case Online
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 11. AN INITIATIVE OF CRC BHARAT ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-4 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
              Parent Organization
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              An Initiative of CRC Bharat
            </h2>
            <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
              Voice of Consumer is a media initiative of the{" "}
              <strong className="text-blue-900 font-bold">
                Consumer Rights Council (CRC Bharat)
              </strong>
              , an organisation working towards consumer awareness, consumer education, and consumer
              protection.
            </p>
            <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
              Through this initiative, CRC Bharat seeks to extend its public-awareness mission
              through journalism and information sharing, giving greater visibility to consumer
              concerns and issues of public importance.
            </p>
          </div>

          <div className="shrink-0 flex flex-col gap-3 w-full md:w-auto">
            <Link
              to="/aboutus"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-bold text-sm shadow-sm transition-all"
            >
              <span>Explore CRC Bharat</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/become-a-member"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm transition-colors"
            >
              <span>Membership Programs</span>
            </Link>
          </div>
        </div>
      </section>

  

  
    </div>
  );
};

export default VoiceOfConsumers;
